/**
 * The state behind one answer's feedback affordance (PRD-18 AGM-120/121).
 *
 * Three small flows share one row of controls — the thumb itself, the optional note, and the
 * offer to send the question to the agent's owner — and each can be in flight or have failed
 * independently. Written as a reducer because the interesting rules are the ones between the
 * flows, and they are invisible when the same logic is spread across five `useState` calls:
 *
 *  - the note and the share panel belong to a vote, so changing the vote retracts the offer;
 *  - a panel that failed keeps what the person typed (nothing typed is ever discarded by a
 *    network error — that is the difference between "try again" and "type it again");
 *  - a second click on the thumb already recorded does nothing. Feedback is a record, not a
 *    switch, and an accidental double-click that silently un-rated an answer would be
 *    indistinguishable from the click never landing.
 */

export const NOTE_MAX = 1000
export const QUESTION_MAX = 2000

export type Vote = 'up' | 'down'

/** `sent` is terminal for a panel: it collapses to a confirmation and cannot be re-submitted. */
type PanelStatus = 'closed' | 'open' | 'sending' | 'sent' | 'failed'

export type FeedbackState = {
  vote: Vote | null
  voteStatus: 'idle' | 'sending' | 'failed'
  note: string
  notePanel: PanelStatus
  question: string
  sharePanel: PanelStatus
}

export type FeedbackAction =
  | { type: 'vote'; vote: Vote }
  | { type: 'vote_ok' }
  | { type: 'vote_failed' }
  | { type: 'note_open' }
  | { type: 'note_change'; value: string }
  | { type: 'note_send' }
  | { type: 'note_ok' }
  | { type: 'note_failed' }
  | { type: 'note_close' }
  | { type: 'share_open'; question: string }
  | { type: 'share_change'; value: string }
  | { type: 'share_send' }
  | { type: 'share_ok' }
  | { type: 'share_failed' }
  | { type: 'share_close' }

export const initialFeedbackState = (vote: Vote | null = null): FeedbackState => ({
  vote,
  voteStatus: 'idle',
  note: '',
  notePanel: 'closed',
  question: '',
  sharePanel: 'closed'
})

export const feedbackReducer = (state: FeedbackState, action: FeedbackAction): FeedbackState => {
  switch (action.type) {
    case 'vote':
      // Re-clicking the recorded vote is inert; so is voting while a vote is in flight.
      if (state.voteStatus === 'sending' || state.vote === action.vote) return state
      return {
        ...state,
        vote: action.vote,
        voteStatus: 'sending',
        // Both panels hung off the previous vote. A note written about "not helpful" must not
        // silently arrive attached to "helpful", and the share offer only exists for a
        // thumbs-down, so changing the vote closes and clears them.
        note: '',
        notePanel: 'closed',
        question: '',
        sharePanel: 'closed'
      }

    case 'vote_ok':
      return { ...state, voteStatus: 'idle' }

    case 'vote_failed':
      // The vote is rolled back, not left showing as recorded: a pressed thumb the server
      // never received is a lie about what the owner will see.
      return { ...state, vote: null, voteStatus: 'failed' }

    case 'note_open':
      return state.vote === null || state.notePanel === 'sent' ? state : { ...state, notePanel: 'open' }

    case 'note_change':
      return state.notePanel === 'sending' || state.notePanel === 'sent'
        ? state
        : { ...state, note: action.value.slice(0, NOTE_MAX), notePanel: 'open' }

    case 'note_send':
      return state.note.trim() === '' || state.notePanel === 'sending' ? state : { ...state, notePanel: 'sending' }

    case 'note_ok':
      return { ...state, notePanel: 'sent' }

    case 'note_failed':
      return { ...state, notePanel: 'failed' }

    case 'note_close':
      return state.notePanel === 'sending' ? state : { ...state, notePanel: 'closed', note: '' }

    case 'share_open':
      // Offered on an unhelpful answer only: sharing the question the agent handled well
      // would put noise in the owner's queue and teach them nothing.
      return state.vote !== 'down' || state.sharePanel === 'sent'
        ? state
        : { ...state, sharePanel: 'open', question: action.question.slice(0, QUESTION_MAX) }

    case 'share_change':
      return state.sharePanel === 'sending' || state.sharePanel === 'sent'
        ? state
        : { ...state, question: action.value.slice(0, QUESTION_MAX), sharePanel: 'open' }

    case 'share_send':
      return state.question.trim() === '' || state.sharePanel === 'sending'
        ? state
        : { ...state, sharePanel: 'sending' }

    case 'share_ok':
      return { ...state, sharePanel: 'sent' }

    case 'share_failed':
      return { ...state, sharePanel: 'failed' }

    case 'share_close':
      return state.sharePanel === 'sending' ? state : { ...state, sharePanel: 'closed', question: '' }

    default:
      return state
  }
}

/** The share offer is visible only while a thumbs-down stands and nothing has been sent yet. */
export const showsShareOffer = (state: FeedbackState, agentHasKnowledge: boolean): boolean =>
  agentHasKnowledge && state.vote === 'down' && state.sharePanel === 'closed'
